import React, { FC, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { LegalInfoWithId } from '../../types/forms';
import LegalTab from '../../components/applicationFormTabs/LegalTab';

const LegalForm: FC = () => {
  const { token } = useParams<Record<string, string>>();
  
  const [formData, setFormData] = useState<LegalInfoWithId | null>(null);
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<'saved' | 'error' | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);

  const fetchLegalFormData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/api/counsellors/public/legal-form/${token}`);
      const counsellorData = response.data;
      
      // Check if form is expired
      if (counsellorData.legalFormExpiryDate && new Date(counsellorData.legalFormExpiryDate) < new Date()) {
        setError('This form has expired. Please contact the administrator for a new link.');
        setLoading(false);
        return;
      }

      // Check if form is already submitted and not an update request
      // (the server flags isUpdateRequest when a fresh token+expiry exists)
      const isUpdateRequest = !!counsellorData.isUpdateRequest;
      if (counsellorData.legalFormSubmitted && !isUpdateRequest) {
        setIsSubmitted(true);
        setError('This form has already been submitted and is awaiting review.');
        setLoading(false);
        return;
      }

      setExpirationDate(counsellorData.legalFormExpiryDate ? new Date(counsellorData.legalFormExpiryDate) : null);
      setAdminNotes(counsellorData.legalInfoAdminNotes || '');
      
      // Get existing legal data if it exists
      const existingLegal = counsellorData.application?.legal || {};
      
      const legalData: LegalInfoWithId = {
        _id: counsellorData._id,
        dbsCheck: existingLegal.dbsCheck || false,
        dbsType: existingLegal.dbsType,
        dbsNumber: existingLegal.dbsNumber || '',
        dbsIssueDate: existingLegal.dbsIssueDate,
        dbsExpiryDate: existingLegal.dbsExpiryDate,
        dbsCertificate: existingLegal.dbsCertificate || null,
        dbsUpdateServiceId: existingLegal.dbsUpdateServiceId || '',
        dbsUpdateServiceConsent: existingLegal.dbsUpdateServiceConsent || false,
        nationality: existingLegal.nationality || '',
        workPermit: existingLegal.workPermit || false,
        workPermitDetails: existingLegal.workPermitDetails || '',
        workPermitExpiryDate: existingLegal.workPermitExpiryDate,
        workPermitNoExpiry: existingLegal.workPermitNoExpiry || false,
        insuranceCertificate: existingLegal.insuranceCertificate || null,
        insuranceNumber: existingLegal.insuranceNumber || '',
        insuranceExpiryDate: existingLegal.insuranceExpiryDate,
        insuranceProvider: existingLegal.insuranceProvider || '',
        insuranceCoverage: existingLegal.insuranceCoverage || '',
        insuranceDetails: existingLegal.insuranceDetails || '',
        convictions: existingLegal.convictions || false,
        convictionsDetails: existingLegal.convictionsDetails || '',
        criminalInvestigation: existingLegal.criminalInvestigation || false,
        criminalInvestigationDetails: existingLegal.criminalInvestigationDetails || '',
        // Fields moved from education
        supervisorName: existingLegal.supervisorName || '',
        supervisorPhoneNumber: existingLegal.supervisorPhoneNumber || '',
        bacpNumber: existingLegal.bacpNumber || '',
        onlineLearningCourse: existingLegal.onlineLearningCourse || false,
        onlineLearningDetails: existingLegal.onlineLearningDetails || '',
        onlineLearningCertificate: existingLegal.onlineLearningCertificate
      };

      setFormData(legalData);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching legal form data:', error);
      setError(error.response?.data?.error || 'Error loading form data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError('No token provided');
      setLoading(false);
      return;
    }
    
    fetchLegalFormData();
  }, [token, fetchLegalFormData]);

  const handleLegalInfoChange = (field: keyof LegalInfoWithId, value: any) => {
    if (!formData) return;
    
    setFormData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const validateLegalInfo = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!formData) {
      errors.push('No form data available');
      return { isValid: false, errors };
    }

    // Required fields validation
    if (!formData.nationality) errors.push('Nationality is required');
    
    // Supervisor information
    if (!formData.supervisorName) errors.push('Supervisor name is required');
    if (!formData.supervisorPhoneNumber) errors.push('Supervisor phone number is required');
    
    // BACP information
    if (!formData.bacpNumber) errors.push('BACP / NCPS number is required');
    
    // Online learning validation
    if (formData.onlineLearningCourse && !formData.onlineLearningDetails) {
      errors.push('Online learning details are required');
    }
    if (formData.onlineLearningCourse && !formData.onlineLearningCertificate) {
      errors.push('Online learning certificate is required');
    }
    
    // DBS validation (DBS is mandatory)
    if (!formData.dbsCheck) {
      errors.push('DBS check is mandatory for all counsellors');
    }
    
    if (formData.dbsCheck) {
      if (!formData.dbsType) {
        errors.push('Please select DBS type (Certificate or Update Service)');
      }
      if (!formData.dbsNumber) errors.push('DBS number is required');
      
      if (formData.dbsType === 'certificate') {
        if (!formData.dbsIssueDate) errors.push('DBS certificate issue date is required');
        if (!formData.dbsCertificate) errors.push('DBS certificate upload is required');
      } else if (formData.dbsType === 'update_service') {
        if (!formData.dbsUpdateServiceId) errors.push('DBS Update Service ID is required');
        if (!formData.dbsUpdateServiceConsent) errors.push('Consent to DBS Update Service checks is required');
      }
    }
    
    // Insurance validation (always required)
    if (!formData.insuranceProvider) errors.push('Insurance provider is required');
    if (!formData.insuranceNumber) errors.push('Insurance policy number is required');
    if (!formData.insuranceExpiryDate) errors.push('Insurance expiry date is required');
    if (!formData.insuranceCertificate) errors.push('Insurance certificate is required');
    
    // Work permit validation
    if (formData.workPermit && !formData.workPermitDetails) {
      errors.push('Work permit details are required');
    }
    if (formData.workPermit && !formData.workPermitNoExpiry && !formData.workPermitExpiryDate) {
      errors.push('Work permit expiry date is required unless the permit has no expiry date');
    }
    
    // Convictions validation
    if (formData.convictions && !formData.convictionsDetails) {
      errors.push('Conviction details are required');
    }
    
    // Criminal investigation validation
    if (formData.criminalInvestigation && !formData.criminalInvestigationDetails) {
      errors.push('Criminal investigation details are required');
    }

    return { isValid: errors.length === 0, errors };
  };

  const handleSaveLegalInfo = async () => {
    if (!formData || !token) return;
    
    try {
      setSaveStatus(null);
      await api.put(`/api/counsellors/public/legal-form/${token}/save`, formData);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving legal information:', error);
      setSaveStatus('error');
    }
  };

  const handleSubmitLegalInfo = async () => {
    if (!formData || !token) return;
    
    const { isValid, errors } = validateLegalInfo();
    
    if (!isValid) {
      setValidationErrors(errors);
      setValidationDialogOpen(true);
      return;
    }
    
    try {
      setSubmitting(true);
      await api.post(`/api/counsellors/public/legal-form/${token}/submit`, formData);
      setIsSubmitted(true);
      setSubmitting(false);
    } catch (error) {
      console.error('Error submitting legal information:', error);
      setError('Failed to submit legal information. Please try again later.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          If you believe this is an error, please contact the administrator.
        </Typography>
      </Container>
    );
  }

  if (isSubmitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Your legal information has been submitted successfully.
        </Alert>
        <Typography variant="body1" paragraph>
          Thank you for submitting your legal information. An administrator will review your submission and may contact you if any additional information is needed.
        </Typography>
        <Typography variant="body1">
          You may now close this window.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Legal Information Form
        </Typography>
        
        {adminNotes && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Administrator Notes:
            </Typography>
            <Typography variant="body1">{adminNotes}</Typography>
          </Alert>
        )}
        
        {expirationDate && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This form will expire on {expirationDate.toLocaleDateString()}
          </Alert>
        )}
        
        {formData && (
          <>
            <LegalTab
              formData={formData}
              onChange={handleLegalInfoChange}
              onSave={handleSaveLegalInfo}
              saveStatus={saveStatus}
              standalone={true}
              token={token}
            />
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitLegalInfo}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Legal Information'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
      
      {/* Validation Error Dialog */}
      <Dialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        aria-labelledby="validation-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="validation-dialog-title" sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
          Please Complete Required Fields
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              The following fields need to be completed before submitting:
            </Typography>
            <List>
              {validationErrors.map((error, index) => (
                <ListItem key={index}>
                  <ListItemText primary={error} />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LegalForm;
